<template>
  <v-navigation-drawer
    v-model:rail="appStateStore.navCollapsed"
    :model-value="showNav"
    width="264"
    rail-width="82"
    floating
    permanent
    touchless>
    <template #prepend>
      <logo @clicked="resetBreadcrumbs" />
      <projects-modal
        v-if="projectsModal"
        :value="showProjectsModal"
        :name="projectsModal.name"
        :type="projectsModal.type"
        @close="showProjectsModal = false" />
    </template>

    <nav-collapsed
      v-if="appStateStore.navCollapsed"
      :nav-items="navItems"
      :is-active-header="isActiveHeader"
      :is-active="isActive"
      @navigate="navigate" />

    <nav-expanded
      v-else
      :nav-items="navItems"
      :is-active-header="isActiveHeader"
      :is-active="isActive"
      @navigate="navigate" />

    <!-- Append to bottom for expanded menu -->
    <template v-if="!appStateStore.navCollapsed" #append>
      <v-divider />
      <div class="d-flex justify-center marketing-link">
        <a :href="getLink('home')" target="_blank">
          Visit {{ marketingLinkLabel }}
        </a>
        <v-icon
          size="x-small"
          icon="mdi-arrow-top-right"
          color="prominent"
          class="ml-1 mb-1" />
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script>
import { mapStores } from "pinia";
import { useAppStateStore } from "@/stores/AppStateStore";
import { killSwitchKeyFromRoute } from "@/helpers/platformFeatures";
import { usePlatformFeatureStore } from "@/stores/PlatformFeatureStore";
import { allDashboards } from "@/helpers/getDashboardsAndProjects";
import { getLink } from "@/helpers/links";
import { userTierMixin } from "@/mixins/UserTier";
import Logo from "@/layouts/Logo.vue";
import NavCollapsed from "@/layouts/Nav/NavCollapsed.vue";
import NavExpanded from "@/layouts/Nav/NavExpanded.vue";
import ProjectsModal from "@/components/ProjectsModal.vue";
import { featureFlags } from "@/lib/feature_flags/flags";

export default {
  name: "PlatformNav",
  components: {
    Logo,
    NavCollapsed,
    NavExpanded,
    ProjectsModal,
  },
  mixins: [userTierMixin],
  data: () => ({
    showProjectsModal: false,
    projectsModal: undefined,
    appClientAdminEnabled: false,
    systemControlsEnabled: false,
    controlCenterEnabled: false,
  }),
  computed: {
    ...mapStores(useAppStateStore, usePlatformFeatureStore),
    showNav() {
      const nonNavRoutes = [
        "login",
        "forgot_password",
        "reset_password",
        "maintenance",
      ];
      return !nonNavRoutes.includes(this.$route.name);
    },
    navItems() {
      return this.$router.options.routes[0].children
        .filter(
          (r) =>
            !!r.meta?.menuIcon &&
            !!r.meta?.breadcrumb &&
            !this.isDisabled(r.meta),
        )
        .reduce((acc, r) => {
          const key = r.path.split("/").pop();
          const items = this.navSubItems(key, r.children);
          if (items.length) {
            acc[key] = {
              icon: r.meta.menuIcon,
              items: items,
            };
          }
          return acc;
        }, {});
    },
    marketingLinkLabel() {
      return this.getLink("home").replace("https://www.", "");
    },
  },
  async mounted() {
    this.appClientAdminEnabled = await featureFlags.flagIsEnabled(
      "enable_app_client_admin",
    );
    this.systemControlsEnabled = await featureFlags.flagIsEnabled(
      "enable_system_controls",
    );
    this.controlCenterEnabled = await featureFlags.flagIsEnabled(
      "enable_control_center",
    );
  },
  methods: {
    getLink,
    isDisabled(meta) {
      if (!meta?.disabled) return false;
      return typeof meta.disabled === "function"
        ? meta.disabled()
        : meta.disabled;
    },
    authorized(route) {
      if (route.path === "campaign-effectiveness") {
        return this.hasCampaignEffectiveness;
      }
      if (route.path === "research-studio" || route.path === "question-bank") {
        return this.hasResearchStudio;
      }
      if (!route.meta.permissions || route.meta.permissions.length === 0) {
        return true;
      }
      if (route.meta.requireAllPermissions) {
        return route.meta.permissions.every(
          (p) => this.$can(p) || this.$hasProduct(p),
        );
      } else {
        return route.meta.permissions.some(
          (p) => this.$can(p) || this.$hasProduct(p),
        );
      }
    },
    navSubItems(key, routes) {
      const items = [];
      routes.forEach((route) => {
        if (route.redirect) return;
        if (!this.authorized(route) || this.isDisabled(route.meta)) return;
        if (this.platformFeatureStore.isDisabled(killSwitchKeyFromRoute(route)))
          return;
        if (
          key === "control-center" &&
          !this.appClientAdminEnabled &&
          route.name === "permissioning"
        )
          return;
        if (
          key === "control-center" &&
          !this.systemControlsEnabled &&
          route.name === "system_controls"
        )
          return;
        if (
          key === "control-center" &&
          !this.controlCenterEnabled &&
          ["download_center", "knowledge_center", "audience_catalog"].includes(
            route.name,
          )
        )
          return;

        let title = route.meta.menuLabel || this.$filters.titleize(route.name);
        title = route.meta.beta ? `${title} (Beta)` : title;
        title = title.replace("Tv", "TV");
        title = title.replace("Vamos", "VAMOS");

        items.push({
          color: route.meta.color,
          title: title,
          route: route.meta.breadcrumb?.to || { name: route.name },
        });
      });

      items.push(...this.dashboardItems(key));
      return items;
    },
    dashboardItems(key) {
      const dashboardConfig = {
        measurement: [
          { id: "paid_media", title: "Paid Media" },
          { id: "earned_media", title: "Earned Media" },
        ],
        planning: [
          { id: "linear_optimization", title: "Linear Optimization" },
          { id: "booking_optimization", title: "Booking Optimization" },
        ],
      };

      const items = [];
      const dashboards = dashboardConfig[key] || [];

      dashboards.forEach(({ id, title }) => {
        if (allDashboards(id).length) {
          items.push({
            title,
            modal: {
              name: id,
              type: "tableau",
              route: { name: id },
            },
          });
        }
      });

      return items;
    },
    isActiveHeader(navItem) {
      return navItem.items.some((item) => this.isActive(item));
    },
    isActive(item) {
      if (!item.route) return;
      return this.$route.name === item.route.name;
    },
    navigate(item) {
      if (item.route) {
        this.resetBreadcrumbs();
        this.$router.push(item.route);
      } else if (item.modal) {
        this.openModal(item.modal);
      }
    },
    resetBreadcrumbs() {
      this.appStateStore.breadcrumbs = [];
    },
    openModal(modalInfo) {
      this.showProjectsModal = true;
      this.projectsModal = {
        ...modalInfo,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.v-navigation-drawer {
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.1px;
  z-index: 120;
  background-color: rgb(var(--v-theme-background-gray));
  color: rgb(var(--v-theme-prominent));
  border-right: 1px solid rgb(var(--v-theme-prominent-lighten-5));
  .marketing-link {
    margin: 16px 0;
    a {
      text-decoration: none;
      color: rgb(var(--v-theme-prominent));
    }
  }
}
</style>
